import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { clientMutation, clientQuery } from "../../../../../../../core/data/GraphQLApi";
import { endpoints } from "../../../../../../../core/defaultValues";
import { ExceptionManager } from "../../../../../../../core/logManager";
import { resetALL } from "../../../../../../../.globals/redux/actions";

const INIT_STATE = {
  open: false,
  loading: false,
  loadingContacts: false,
  unit: null,
  error: null,
  id: null,
  updateManageTemperatureResult: null,
  contacts: [],
  units: [],
  loadingUnits: false
};

const route = "modules/manage/modals/createEditManageTemperature/redux/slice";

export const manageTemperatureGetContacts = createAsyncThunk(
  "manage/createEditManageTemperature/manageTemperatureGetContacts",
  async (_, { rejectWithValue }) => {
    try {
      const query = `
            query getAllContacts ($openSearch: String, $limit: Int!, $offset: Int) {
              getAllContacts (openSearch: $openSearch, limit: $limit, offset: $offset) {
                  items {
                      id
                      title
                  }
              }
          }
      `;
      const response = await clientQuery(query, { limit: 0 }, endpoints.GRAPHQL_GENERAL);
      return response?.getAllContacts?.items;
    } catch (exc) {
      ExceptionManager(exc, route, "manageTemperatureGetContacts");
      return rejectWithValue(exc);
    }
  }
);

export const manageTemperatureGetAvailableUnits = createAsyncThunk(
  "manage/createEditManageTemperature/manageTemperatureGetAvailableUnits",
  async (_, { rejectWithValue }) => {
    try {
      const query = `
      query searchUnits ($withTempSensorOnly: Boolean) {
        searchUnits (withTempSensorOnly: $withTempSensorOnly) {
                id
                label
                typeId
                type
                hid
            }
        }
      `;
      const response = await clientQuery(
        query,
        { withTempSensorOnly: false },
        endpoints.GRAPHQL_GENERAL
      );
      return response?.searchUnits;
    } catch (exc) {
      ExceptionManager(exc, route, "manageTemperatureGetContacts");
      return rejectWithValue(exc);
    }
  }
); 

export const manageCreateEditTemperature = createAsyncThunk(
  "manage/createEditManageTemperature/createEditManageTemperature",
  async (payload, { rejectWithValue }) => {
    const mutations = [];
    const alertDeleteMutations = [];
    try {
      const { temperatures, alertsToDelete } = payload;

      if (alertsToDelete?.length) {
        try {
          alertsToDelete.forEach((alertToDelete) => {
            alertDeleteMutations.push(
              clientMutation(
                `mutation deleteSystemAlert ($id: String!) {
                    deleteSystemAlert (id: $id)
                }`,
                { id: alertToDelete.id },
                endpoints.GRAPHQL_GENERAL
              )
            );
          });
          await Promise.all(alertDeleteMutations);
        } catch (exc) {
          ExceptionManager(exc, route, "manageDeleteAlertServices");
          return rejectWithValue(exc);
        }
      }
      let responses = null;

      if (temperatures?.length) {
        temperatures.forEach((temperature) => {
          if (temperature?.id) {
            mutations.push(
              clientMutation(
                `mutation updateSystemAlert ($id: String!, $name: String, $type: enumAlertType, $config: AWSJSON, $severity: Severity!, $summary: String, $triggers: [SystemAlertTriggerInput], $assignedTo: [SystemAlertAssignmentInput], $schedule: SystemAlertScheduleInput, $subscribers: [AlertSubscriberInput]) {
                      alert: updateSystemAlert (id: $id, name: $name, type: $type, config: $config, severity: $severity, summary: $summary, triggers: $triggers, assignedTo: $assignedTo, schedule: $schedule, subscribers: $subscribers) {
                        id
                      }
                    }`,

                temperature,
                endpoints.GRAPHQL_GENERAL
              )
            );
          } else {
            mutations.push(
              clientMutation(
                `mutation createSystemAlert ($name: String!, $type: enumAlertType!, $config: AWSJSON, $severity: Severity!, $summary: String, $triggers: [SystemAlertTriggerInput], $assignedTo: [SystemAlertAssignmentInput], $schedule: SystemAlertScheduleInput, $subscribers: [AlertSubscriberInput]) {
                          alert: createSystemAlert(name: $name, type: $type, config: $config, severity: $severity, summary: $summary, triggers: $triggers, assignedTo: $assignedTo, schedule: $schedule, subscribers: $subscribers) {
                            id
                          }
                        }`,
                temperature,
                endpoints.GRAPHQL_GENERAL
              )
            );
          }
        });
        responses = await Promise.all(mutations);
        return responses.every((response) => response?.alert?.id);
      }
    } catch (exc) {
      ExceptionManager(exc, route, "manageCreateEditTemperature");
      return rejectWithValue(exc);
    }
  }
);

const manageCreateEditManageTemperatureRedux = createSlice({
  name: "manage/createEditManageTemperature",
  initialState: INIT_STATE,
  reducers: {
    reset(state, action) {
      return action.payload ? { ...state, ...action.payload } : { ...INIT_STATE };
    },
    openModalCreateEditManageTemperature(state, action) {
      state.open = true;
      state.unit = action?.payload;
    }
  },
  extraReducers: (builder) => {
    builder.addCase(resetALL, () => INIT_STATE);

    //manageTemperatureGetContacts
    builder.addCase(manageTemperatureGetContacts.pending, (state) => {
      state.loadingContacts = true;
      state.contacts = [];
    });
    builder.addCase(manageTemperatureGetContacts.fulfilled, (state, action) => {
      state.loadingContacts = false;
      state.contacts = action.payload;
    });
    builder.addCase(manageTemperatureGetContacts.rejected, (state, action) => {
      state.loadingContacts = false;
      state.error = action.payload;
    });

    //manageCreateEditTemperature
    builder.addCase(manageCreateEditTemperature.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(manageCreateEditTemperature.fulfilled, (state, action) => {
      state.loading = false;
      state.updateManageTemperatureResult = action.payload;
    });
    builder.addCase(manageCreateEditTemperature.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    //manageTemperatureGetAvailableUnits
    builder.addCase(manageTemperatureGetAvailableUnits.pending, (state) => {
      state.loadingUnits = true;
    });
    builder.addCase(manageTemperatureGetAvailableUnits.fulfilled, (state, action) => {
      state.loadingUnits = false;
      state.units = action.payload;
    });
    builder.addCase(manageTemperatureGetAvailableUnits.rejected, (state, action) => {
      state.loadingUnits = false;
      state.error = action.payload;
    });
  }
});

// Extract and export the action creators object and the reducer
export const { actions, reducer } = manageCreateEditManageTemperatureRedux;

// Extract and export each action creator by name
export const { reset, openModalCreateEditManageTemperature } = actions;

// Export the reducer, either as a default or named export
export default reducer;
